/**
 * Transaction Tracker Service
 * Tracks game client transactions for Sentry analytics
 *
 * Tracks:
 * - egt.lobby.transactions.game_client.game_name: Full name of the opened game
 * - egt.lobby.transactions.game_client.game_key: Key identifier of the opened game
 * - egt.lobby.transactions.game_client.game_opened: 1 if game was opened, 0 otherwise
 * - egt.lobby.transactions.game_client.game_opened_from_section: Section name where game was opened from
 * - egt.lobby.transactions.game_client.game_opened_from_multi_view: 1 if opened from multi view, 0 otherwise
 * - egt.lobby.transactions.game_client.game_opened_from_single_view: 1 if opened from single view, 0 otherwise
 */

interface GameClientTransaction {
    game_name: string;
    game_key: string;
    game_opened: number;
    game_opened_from_section: string;
    game_opened_from_multi_view: number;
    game_opened_from_single_view: number;
}

class TransactionTracker {
    private static instance: TransactionTracker;
    private gameClient: GameClientTransaction;

    private constructor() {
        // Initialize with default values
        this.gameClient = {
            game_name: '',
            game_key: '',
            game_opened: 0,
            game_opened_from_section: '',
            game_opened_from_multi_view: 0,
            game_opened_from_single_view: 0,
        };
    }

    public static getInstance(): TransactionTracker {
        if (!TransactionTracker.instance) {
            TransactionTracker.instance = new TransactionTracker();
        }
        return TransactionTracker.instance;
    }

    /**
     * Track a game open transaction
     * @param gameName - The full name of the game
     * @param gameKey - The game key identifier
     * @param sectionName - The section name where the game was opened from
     * @param viewMode - The view mode ('multi' or 'single')
     */
    public trackGameOpen(gameName: string, gameKey: string, sectionName: string, viewMode: 'multi' | 'single'): void {
        this.gameClient = {
            game_name: gameName,
            game_key: gameKey,
            game_opened: 1,
            game_opened_from_section: sectionName,
            game_opened_from_multi_view: viewMode === 'multi' ? 1 : 0,
            game_opened_from_single_view: viewMode === 'single' ? 1 : 0,
        };

        if (process.env['NODE_ENV'] === 'development') {
            console.log(`[TransactionTracker] Game opened: ${gameName} (${gameKey}) from ${sectionName} in ${viewMode} view`);
        }
    }

    /**
     * Get game client transaction data formatted for Sentry
     */
    public getFormattedTransactions(): Record<string, string | number> {
        return {
            'egt.lobby.transactions.game_client.game_name': this.gameClient.game_name,
            'egt.lobby.transactions.game_client.game_key': this.gameClient.game_key,
            'egt.lobby.transactions.game_client.game_opened': this.gameClient.game_opened,
            'egt.lobby.transactions.game_client.game_opened_from_section': this.gameClient.game_opened_from_section,
            'egt.lobby.transactions.game_client.game_opened_from_multi_view': this.gameClient.game_opened_from_multi_view,
            'egt.lobby.transactions.game_client.game_opened_from_single_view': this.gameClient.game_opened_from_single_view,
        };
    }

    /**
     * Check if a game has been opened during this session
     */
    public hasGameOpened(): boolean {
        return this.gameClient.game_opened === 1;
    }

    /**
     * Get the current game client data (for debugging)
     */
    public getGameClientData(): GameClientTransaction {
        return { ...this.gameClient };
    }

    /**
     * Clear transaction data (useful for new sessions)
     */
    public clear(): void {
        this.gameClient = {
            game_name: '',
            game_key: '',
            game_opened: 0,
            game_opened_from_section: '',
            game_opened_from_multi_view: 0,
            game_opened_from_single_view: 0,
        };
    }
}

// Export singleton instance
export const transactionTracker = TransactionTracker.getInstance();
